import { useEffect, useState } from 'react'

import _ from 'lodash'
import { DateTime } from 'luxon'
import Countdown from 'react-countdown'
import VerificationInput from 'react-verification-input'
import toastr from 'toastr'

import { env } from '@/env'
import { useAuthContext } from '@/features/auth/model/auth-context'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { getErrorMessage, showErrorAlert } from '@/shared/lib/errors'
import { Button } from '@/shared/ui'
import { alert } from '@/shared/ui/dialog'





const isDev = env.NODE_ENV == 'development'

export const ConfirmationFields = ({
  onValidateToken,
  resendTokenApiUrl = '/auth/request-confirmation-token'
}) => {
  const {
    isLoading,
    setIsLoading,
    httpClient,
    styleConfig,
    currentEmail,
    goToSignIn,
    currentPassword
  } = useAuthContext()
  const [token, setToken] = useState('')
  const tokenIsValid = token && token.length >= 6
  const [resendToken, setResendToken] = useState({
    iterator: 1,
    date: DateTime.local().toISO()
  })
  const [name, domain] = currentEmail ? currentEmail.split('@') : []
  const hiddenEmail = isDev
    ? currentEmail
    : name &&
      currentEmail &&
      `${name[0]}${new Array(name.length).join('*')}@${domain}`

  const { t } = useTranslation()

  const handleResendToken = async () => {
    if (isLoading) return

    setIsLoading(true)

    try {
      await httpClient.post(resendTokenApiUrl, {
        email: currentEmail
      })

      toastr.success(
        t(
          'auth.newCodeSentSuccessfully',
          {},
          { fallback: 'Novo código enviado com sucesso' }
        )
      )

      setResendToken(prevState => ({
        iterator: prevState.iterator + 1,
        date: DateTime.local()
          .plus({ seconds: 30 * prevState.iterator })
          .toISO()
      }))
    } catch (error) {
      const { message } = getErrorMessage(error)

      if (message == 'email já foi confirmado') {
        await alert({
          variant: 'info',
          title: t(
            'auth.emailAlreadyConfirmed',
            {},
            { fallback: 'Email já confirmado' }
          ),
          description: t(
            'auth.yourEmailAlreadyConfirmed',
            {},
            {
              fallback:
                'Seu email já foi confirmado, você pode fazer login agora'
            }
          )
        })

        goToSignIn({ filled: true })
      } else showErrorAlert(error)
    }

    setIsLoading(false)
  }
  const handleValidateToken = () => {
    if (!tokenIsValid) return

    onValidateToken(token)
    setToken('')
  }

  useEffect(() => {
    if (tokenIsValid) handleValidateToken()
  }, [tokenIsValid])

  return (
    <div className="flex flex-col items-center justify-center grow lg:px-10 pb-15 lg:pb-20">
      <div className="mb-10">
        <div className="font-bold text-left text-gray-900 text-sm mb-2 ml-1 block">
          {t(
            'auth.enterYour6DigitCode',
            {},
            { fallback: 'Digite seu código de 6 dígitos' }
          )}
        </div>
        {/* `removeDefaultStyles` e `disabled` como props diretas não existem na
            API v4 desta lib: eram ignorados em silêncio — e por isso o campo
            seguia editável enquanto o código era validado. O desabilitar vai em
            `inputProps`, que é onde a lib repassa para o `<input>`. */}
        <VerificationInput
          classNames={{
            container:
              'flex justify-between mb-2 h-[50px] gap-2 lg:h-[60px] min-w-[300px] w-full',
            character:
              'w-full rounded-[0.625rem] border border-gray-300 bg-white text-2xl font-bold text-center m-0 lg:h-[60px] lg:w-[60px] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand',
            characterInactive: '',
            characterSelected: 'border-primary'
          }}
          placeholder=""
          value={token}
          onChange={value => setToken(value)}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            disabled: isLoading
          }}
        />
      </div>
      <div className="flex mb-13">
        <Button
          type="button"
          variant="primary"
          size="lg"
          className="font-bold"
          disabled={!tokenIsValid}
          onClick={handleValidateToken}
          showTrailingIcon={false}
        >
          {t(
            tokenIsValid ? 'auth.confirmAccount' : 'auth.enterTheCode',
            {},
            { fallback: tokenIsValid ? 'Confirmar conta' : 'Digite o código' }
          )}
        </Button>
      </div>
      <div className="text-center font-semibold text-base">
        <span className="text-gray-700 mr-1">
          {t(
            'auth.didNotReceiveTheCodeRequestANewOne',
            {},
            { fallback: 'Não recebeu o código? Peça um novo' }
          )}
        </span>

        <Countdown
          key={resendToken.iterator}
          date={resendToken.date}
          renderer={({ seconds, completed }) => (
            <>
              {completed ? (
                <span
                  className="cursor-pointer text-primary font-bold"
                  onClick={handleResendToken}
                >
                  {t('auth.clickingHere', {}, { fallback: 'clicando aqui' })}
                </span>
              ) : (
                <>
                  {t(
                    'auth.inSeconds',
                    { seconds },
                    { fallback: `em ${seconds} segundos` }
                  )}
                </>
              )}
            </>
          )}
        />
      </div>
    </div>
  )
}
